import React, { useEffect, useRef, useState, useMemo } from "react";

import { useForm } from "react-hook-form";

import * as Yup from "yup";

import { yupResolver } from "@hookform/resolvers/yup";

import {
  Input,
  Button,
  Checkbox,
  Typography,
} from "@material-tailwind/react";

import {
  EyeSlashIcon,
  EyeIcon,
  DocumentArrowUpIcon,
} from "@heroicons/react/24/outline";

import OTPVerification from "./otp-verification";

import { useDispatch, useSelector } from "react-redux";

import Select from "react-select";

import {
  CountryOption,
  CountrySingleValue,
} from "@/components/CountrySelectComponents";

import {
  fetchCity,
  fetchCountry,
  fetchCountryMobileCode,
} from "@/store/slices/internalUsersSlice";

import FilePreview from "@/components/FilePreview";

import { registerDelivaryPartaner } from "@/store/slices/deliveryUserSlice";

import ShowLoader from "@/components/Loader";

import Toaster from "@/components/Toaster";

import { useNavigate } from "react-router-dom";

import ConfirmationModal from "./confirmation";

import { useTranslation } from "react-i18next";
import CountryDetection from "@/components/CountryDetection";


/* =========================================================
   COMMON REGEX
========================================================= */

const noHtmlRegex = /^(?!.*<[^>]+>).*/;

const notBlankRegex = /\S/;

const noCodeRegex =
  /^(?!.*(javascript:|<script|<\/script>|<style|<\/style>|<iframe|<\/iframe>)).*$/i;

const noConsecutiveSpecialChars =
  /^(?!.*([!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])\1).*$/;

const noLeadingTrailingPunct =
  /^(?!^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])(?<![!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]$).*$/;

const noLeadingSpaceRegex = /^(?!\s).*/;


/* =========================================================
   FILE VALIDATION HELPER
========================================================= */

const fileValidation = (t, label, required = true) =>
  Yup.mixed()
    .nullable()

    .test("required", `${label} ${t("validation.required")}`, (value) => {
      if (!required) return true;

      return value instanceof File;
    })

    .test(
      "fileSize",
      t("validation.fileSize"),
      (value) => {
        if (!value) return !required;

        return value.size <= 5 * 1024 * 1024;
      }
    )

    .test(
      "fileType",
      t("validation.fileType"),
      (value) => {
        if (!value) return !required;

        const allowedTypes = [
          "image/jpeg",
          "image/png",
          "application/pdf",
        ];

        return allowedTypes.includes(value.type);
      }
    );


/* =========================================================
   COMMON STRING SECURITY
========================================================= */

const secureString = (t) =>
  Yup.string()
    .trim()

    .matches(
      noLeadingSpaceRegex,
      t("validation.leadingSpaces")
    )

    .matches(
      noHtmlRegex,
      t("validation.htmlNotAllowed")
    )

    .matches(
      noCodeRegex,
      t("validation.codeNotAllowed")
    )

    .matches(
      noConsecutiveSpecialChars,
      t("validation.consecutiveSpecialChars")
    )

    .matches(
      noLeadingTrailingPunct,
      t("validation.leadingTrailingPunctuation")
    );


/* =========================================================
   VALIDATION SCHEMA
   IMPORTANT:
   Schema is a function because it needs translation `t`
========================================================= */

export const getValidationSchema = (t) =>
  Yup.object({

    /* =====================================================
       NAME
    ===================================================== */

    first_name: Yup.string()

      .required(
        t("validation.firstNameRequired")
      )

      .concat(
        secureString(t)
      )

      .min(
        2,
        t("validation.firstNameMin")
      )

      .max(
        50,
        t("validation.firstNameMax")
      )

      .matches(
        /^[a-zA-Z\s]+$/,
        t("validation.onlyAlphabets")
      ),


    last_name: Yup.string()

      .transform((value) =>
        value === "" ? undefined : value
      )

      .notRequired()

      .concat(
        secureString(t)
      )

      .min(
        2,
        t("validation.lastNameMin")
      )

      .max(
        50,
        t("validation.lastNameMax")
      )

      .matches(
        /^[a-zA-Z\s]+$/,
        t("validation.onlyAlphabets")
      ),


    /* =====================================================
       GENDER
    ===================================================== */

    gender: Yup.string()

      .nullable()

      .transform((value) =>
        value === "" ? null : value
      )

      .required(
        t("validation.genderRequired")
      )

      .oneOf(
        ["Male", "Female", "Other"],
        t("validation.genderInvalid")
      ),


    /* =====================================================
       CONTACT
    ===================================================== */

    country_code: Yup.string()

      .trim()

      .required(
        t("validation.countryCodeRequired")
      )

      .matches(
        /^\+[1-9][0-9]{0,3}$/,
        t("validation.countryCodeInvalid")
      ),


    mobile_number: Yup.string()

      .trim()

      .required(
        t("validation.mobileRequired")
      )

      .matches(
        noLeadingSpaceRegex,
        t("validation.leadingSpaces")
      )

      .matches(
        /^[0-9]+$/,
        t("validation.onlyDigits")
      )

      .min(
        8,
        t("validation.mobileMin")
      )

      .max(
        15,
        t("validation.mobileMax")
      )

      .test(
        "not-all-zeros",
        t("validation.mobileZeros"),
        (value) =>
          value ? !/^0+$/.test(value) : false
      ),


    email: Yup.string()

      .trim()

      .lowercase()

      .required(
        t("validation.emailRequired")
      )

      .matches(
        noLeadingSpaceRegex,
        t("validation.leadingSpaces")
      )

      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        t("validation.emailInvalid")
      )

      .max(
        100,
        t("validation.emailMax")
      )

      .matches(
        noHtmlRegex,
        t("validation.invalidCharacters")
      ),


    /* =====================================================
       PASSWORD
    ===================================================== */

    password: Yup.string()

      .required(
        t("validation.passwordRequired")
      )

      .test(
        "password-validation",
        function (value) {

          if (!value) return true;

          let errors = [];

          if (value.length < 8) {
            errors.push(
              `• ${t("validation.passwordMin")}`
            );
          }

          if (value.length > 15) {
            errors.push(
              `• ${t("validation.passwordMax")}`
            );
          }

          if (!/[A-Z]/.test(value)) {
            errors.push(
              `• ${t("validation.passwordUppercase")}`
            );
          }

          if (!/[a-z]/.test(value)) {
            errors.push(
              `• ${t("validation.passwordLowercase")}`
            );
          }

          if (!/[0-9]/.test(value)) {
            errors.push(
              `• ${t("validation.passwordNumber")}`
            );
          }

          if (!/[@$!%*?&#]/.test(value)) {
            errors.push(
              `• ${t("validation.passwordSpecial")}`
            );
          }

          if (!noHtmlRegex.test(value)) {
            errors.push(
              `• ${t("validation.invalidCharacters")}`
            );
          }

          if (errors.length > 0) {
            return this.createError({
              message: errors.join("\n"),
            });
          }

          return true;
        }
      ),


    password_confirmation: Yup.string()

      .required(
        t("validation.confirmPasswordRequired")
      )

      .matches(
        noLeadingSpaceRegex,
        t("validation.leadingSpaces")
      )

      .oneOf(
        [Yup.ref("password")],
        t("validation.passwordMismatch")
      ),


    /* =====================================================
       ADDRESS
    ===================================================== */

    permanent_address: secureString(t)

      .required(
        t("validation.addressRequired")
      )

      .min(
        10,
        t("validation.addressMin")
      )

      .max(
        100,
        t("validation.addressMax")
      ),


    /* =====================================================
       RESIDENCE
    ===================================================== */

    residence_id: secureString(t)

      .required(
        t("validation.residenceIdRequired")
      )

      .min(
        2,
        t("validation.residenceIdMin")
      )

      .max(
        30,
        t("validation.residenceIdMax")
      ),


    residence_image: fileValidation(
      t,
      t("fields.residenceIdFile")
    ),


    /* =====================================================
       DRIVING LICENCE
    ===================================================== */

    driving_licence_image: fileValidation(
      t,
      t("fields.drivingLicenseFile")
    ),


    /* =====================================================
       VEHICLE
    ===================================================== */

    vehicle_number: Yup.string()

      .transform((value) =>
        value === "" ? null : value
      )

      .nullable()

      .notRequired()

      .matches(
        /^[0-9A-Z\s-]+$/i,
        {
          message: t(
            "validation.vehicleNumberInvalid"
          ),
          excludeEmptyString: true,
        }
      )

      .min(
        4,
        t("validation.vehicleNumberMin")
      )

      .max(
        20,
        t("validation.vehicleNumberMax")
      ),


    /* =====================================================
       INSURANCE
    ===================================================== */

    insurance_image: fileValidation(
      t,
      t("fields.insuranceFile"),
      false
    ),


    vehicle_certificate_image: fileValidation(
      t,
      t("fields.vehicleCertificateFile"),
      false
    ),


    /* =====================================================
       LOCATION
    ===================================================== */

    country_id: Yup.string()

      .required(
        t("validation.countryRequired")
      ),


    city_id: Yup.string()

      .required(
        t("validation.cityRequired")
      ),


    /* =====================================================
       OTHER DOCUMENTS
    ===================================================== */

    other_documents: Yup.mixed()

      .nullable()

      .test(
        "fileType",
        t("validation.otherDocumentsFileType"),
        (value) => {

          if (!value) return true;

          const files = Array.isArray(value)
            ? value
            : [value];

          return files.every((file) => {

            if (typeof file === "string") {
              return true;
            }

            if (
              file?.file &&
              typeof file.file === "string"
            ) {
              return true;
            }

            if (file instanceof File) {
              return (
                file.type.startsWith("image/") ||
                file.type === "application/pdf"
              );
            }

            return false;
          });
        }
      )

      .test(
        "fileCount",
        t("validation.otherDocumentsMax"),
        (value) => {

          if (!value) return true;

          const files = Array.isArray(value)
            ? value
            : [value];

          return files.length <= 10;
        }
      ),


    other_documents_detail: Yup.string()

      .nullable()

      .test(
        "not-only-spaces",
        t("validation.onlySpaces"),
        (value) => {

          if (
            value === null ||
            value === undefined
          ) {
            return true;
          }

          if (value === "") {
            return true;
          }

          return value.trim().length > 0;
        }
      )

      .matches(
        noHtmlRegex,
        t("validation.htmlNotAllowed")
      )

      .matches(
        noCodeRegex,
        t("validation.codeNotAllowed")
      )

      .matches(
        noConsecutiveSpecialChars,
        t("validation.otherDocumentsSpecialChars")
      )

      .matches(
        noLeadingTrailingPunct,
        t("validation.otherDocumentsPunctuation")
      )

      .max(
        100,
        t("validation.otherDocumentsMaxCharacters")
      ),


    /* =====================================================
       TERMS
    ===================================================== */

    agree_terms: Yup.boolean()

      .oneOf(
        [true],
        t("validation.termsRequired")
      )

      .required(
        t("validation.termsRequired")
      ),
  });


/* =========================================================
   INITIAL VALUES
========================================================= */

const initialValues = {

  first_name: "",

  last_name: "",

  gender: null,

  country_code: null,

  mobile_number: null,

  email: null,

  password: null,

  password_confirmation: null,

  permanent_address: null,

  residence_id: null,

  residence_image: null,

  driving_licence: null,

  driving_licence_image: null,

  vehicle_number: null,

  insurance: null,

  insurance_image: null,

  agree_terms: false,

  country_id: null,

  city_id: null,

  vehicle_certificate_image: null,

  other_documents: [],

  other_documents_detail: null,
};


/* =========================================================
   COMPONENT
========================================================= */

export function SignUp() {

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { t, i18n } = useTranslation(["signup", "countries"]);


  /* =======================================================
     STATES
  ======================================================= */

  const [showPassword, setShowPassword] =
    useState(false);

  const [showConfirmPassword, setShowConfirmPassword] =
    useState(false);

  const [openModal, setOpenModal] =
    useState(false);

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState(false);

  const [registrationFormData, setRegistrationFormData] =
    useState({});


  /* =======================================================
     REDUX
  ======================================================= */

  const {
    mobile_country_code,
    cities,
    countries,
  } = useSelector(
    (state) => state.internalUser
  );

  const { loading } = useSelector(
    (state) => state.delivery_user
  );


  /* =======================================================
     TRANSLATED YUP SCHEMA
  ======================================================= */

  const validationSchema = useMemo(
    () => getValidationSchema(t),
    [t]
  );


  /* =======================================================
     FETCH DATA
  ======================================================= */

  useEffect(() => {

    dispatch(
      fetchCountryMobileCode()
    );

    dispatch(
      fetchCountry()
    );

  }, [dispatch]);

  useEffect(() => {
    const erroredFields = Object.keys(errors);

    if (erroredFields.length > 0) {
      trigger(erroredFields);
    }
    
  }, [i18n.language]);


  /* =======================================================
     FILE REFS
  ======================================================= */

  const residenceRef = useRef(null);

  const licenseRef = useRef(null);

  const insuranceRef = useRef(null);

  const vehicleCertificateRef =
    useRef(null);

  const otherDocumentsRef =
    useRef(null);


  /* =======================================================
     FORM
  ======================================================= */

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    setError,
    reset,
    trigger,
  } = useForm({

    defaultValues: initialValues,

    resolver: yupResolver(
      validationSchema
    ),

    mode: "onChange",
  });


  /* =======================================================
     HANDLE LEADING SPACE
  ======================================================= */

  const handleNoLeadingSpace = (
    e,
    fieldName
  ) => {

    const value = e.target.value;

    if (value.startsWith(" ")) {

      setValue(
        fieldName,
        value.trimStart()
      );
    }
  };


  /* =======================================================
     SUBMIT
  ======================================================= */

  const onSubmit = (data) => {

    const formdata =
      JSON.stringify(data);

    localStorage.setItem(
      "develary_partaner_form_data",
      formdata
    );

    dispatch(
      registerDelivaryPartaner(data)
    )
      .unwrap()

      .then((res) => {

        if (res?.status) {

          Toaster.success(
            res?.message ||
            t(
              "messages.registrationInitiated"
            )
          );

          setRegistrationFormData(data);

          setOpenModal(true);

        } else {

          Toaster.error(
            res?.message ||
            t(
              "messages.registrationFailed"
            )
          );

          if (res?.data?.field) {

            setError(
              res.data.field,
              {
                type: "manual",
                message:
                  res?.data?.message ||
                  t(
                    "messages.registrationFailed"
                  ),
              }
            );
          }
        }
      })

      .catch((err) => {

        Toaster.error(
          err?.message ||
          t(
            "messages.registrationFailed"
          )
        );

      });
  };


  /* =======================================================
     CLOSE OTP MODAL
  ======================================================= */

  const closeVerifyOtpModal = () => {

    const isMobileVerified =
      localStorage.getItem(
        "is_mobile_verified"
      ) || false;

    const isEmailVerified =
      localStorage.getItem(
        "is_email_verified"
      ) || false;


    if (
      !isEmailVerified ||
      !isMobileVerified
    ) {

      Toaster.info(
        t(
          "messages.registrationPending"
        )
      );
    }


    localStorage.removeItem(
      "is_mobile_verified"
    );

    localStorage.removeItem(
      "is_email_verified"
    );

    setOpenModal(false);
  };


  /* =======================================================
     SUCCESS
  ======================================================= */

  const success = () => {

    setOpenModal(false);

    reset();

    setIsConfirmationModalOpen(
      true
    );
  };


  /* =======================================================
     CONFIRMED
  ======================================================= */

  const confirmed = () => {

    navigate("/sign-in");
  };


  /* =======================================================
     FILE CHANGE
  ======================================================= */

  const handleFileChange = (
    e,
    field
  ) => {

    const files =
      Array.from(e.target.files || []);


    if (
      field === "other_documents"
    ) {

      const existingDocs =
        watch("other_documents") || [];

      setValue(
        "other_documents",
        [
          ...existingDocs,
          ...files,
        ],
        {
          shouldValidate: true,
        }
      );

    } else {

      setValue(
        field,
        files[0],
        {
          shouldValidate: true,
        }
      );
    }
  };


  /* =======================================================
     WATCH FILES
  ======================================================= */

  const insurance_image =
    watch("insurance_image");

  const driving_licence_image =
    watch("driving_licence_image");

  const residence_image =
    watch("residence_image");

  const vehicle_certificate_image =
    watch(
      "vehicle_certificate_image"
    );

  const other_documents =
    watch("other_documents");


  /* =======================================================
     STYLES
  ======================================================= */

  const inputBaseStyles =
    "!border-t-[#CFD2D9] focus:!border-t-[#0A5D38] placeholder:text-[#2F302C61] shadow-none";

  const labelProps = {
    className:
      "before:content-none after:content-none",
  };


  /* =======================================================
     GENDER OPTIONS
  ======================================================= */

  const genderOptions = [

    {
      value: "Male",
      label: t("gender.male"),
    },

    {
      value: "Female",
      label: t("gender.female"),
    },

    {
      value: "Other",
      label: t("gender.other"),
    },

  ];

  // const translatedCountries = useMemo(() => {
  //   return countries?.map((country) => ({
  //     ...country,
  //     label: t(country.label, { ns: "countries", defaultValue: country.label }),
  //   })) || [];
  // }, [countries, t, i18n.language]);

  // const translatedCities = useMemo(() => {
  //   return cities?.map((city) => ({
  //     ...city,
  //     label: t(city.label ?? city.value, { ns: "countries", defaultValue: city.label }),
  //   })) || [];
  // }, [cities, t, i18n.language]);

  /* =======================================================
     RETURN
  ======================================================= */

  return (

    <div className="w-full min-h-screen bg-white">

      <div className="max-w-5xl mx-auto relative z-10">

        {loading && <ShowLoader />}


        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-5"
        >


          {/* =================================================
              ROW 1 - NAMES
          ================================================= */}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* FIRST NAME */}

            <div className="flex flex-col gap-1.5">

              <Typography
                variant="small"
                className="font-lato text-[#2F302C] text-[16px]"
              >
                {t("fields.firstName")}

                <span className="text-[#FF0000]">
                  *
                </span>
              </Typography>


              <Input
                size="lg"
                placeholder={t(
                  "placeholders.firstName"
                )}
                {...register(
                  "first_name",
                  {
                    onChange: (e) =>
                      handleNoLeadingSpace(
                        e,
                        "first_name"
                      ),
                  }
                )}
                error={
                  !!errors.first_name
                }
                className={
                  inputBaseStyles
                }
                labelProps={
                  labelProps
                }
              />


              {errors.first_name && (

                <span className="text-[#FF0000] text-xs">

                  {
                    errors
                      .first_name
                      .message
                  }

                </span>
              )}

            </div>


            {/* LAST NAME */}

            <div className="flex flex-col gap-1.5">

              <Typography
                variant="small"
                className="font-lato text-[#2F302C] text-[16px]"
              >
                {t("fields.lastName")}
              </Typography>


              <Input
                size="lg"
                placeholder={t(
                  "placeholders.lastName"
                )}
                {...register(
                  "last_name",
                  {
                    onChange: (e) =>
                      handleNoLeadingSpace(
                        e,
                        "last_name"
                      ),
                  }
                )}
                error={
                  !!errors.last_name
                }
                className={
                  inputBaseStyles
                }
                labelProps={
                  labelProps
                }
              />


              {errors.last_name && (

                <span className="text-[#FF0000] text-xs">

                  {
                    errors
                      .last_name
                      .message
                  }

                </span>
              )}

            </div>

          </div>


          {/* =================================================
              ROW 2 - CONTACT
          ================================================= */}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">


            {/* MOBILE */}

            <div className="flex flex-col gap-1.5 font-lato">

              <Typography className="font-lato text-[#2F302C] text-[16px]">

                {t(
                  "fields.mobileNumber"
                )}

                <span className="text-[#FF0000]">
                  *
                </span>

              </Typography>


              <div className="relative flex w-full">

                <Select

                  placeholder={t(
                    "placeholders.countryCode"
                  )}

                  {...register(
                    "country_code"
                  )}

                  onChange={(
                    country_code
                  ) => {

                    setValue(
                      "country_code",
                      country_code?.value,
                      {
                        shouldValidate:
                          true,
                      }
                    );
                  }}

                  styles={{
                    control: (
                      base,
                      state
                    ) => ({
                      ...base,
                      height: "44px",
                      minHeight: "44px",
                      borderColor:
                        errors.country_code
                          ? "#FF0000"
                          : "#CFD2D9",
                      boxShadow:
                        state.isFocused
                          ? "0 0 0 1px #333"
                          : "none",
                      "&:hover": {
                        borderColor:
                          "#90A4AE",
                      },
                    }),
                  }}

                  className="min-w-[150px]"

                  options={
                    mobile_country_code
                  }

                  components={{
                    Option:
                      CountryOption,
                    SingleValue:
                      CountrySingleValue,
                  }}

                />


                <Input
                  size="lg"
                  placeholder={t(
                    "placeholders.mobileNumber"
                  )}

                  {...register(
                    "mobile_number",
                    {
                      onChange: (e) => {

                        handleNoLeadingSpace(
                          e,
                          "mobile_number"
                        );

                        const onlyNums =
                          e.target.value.replace(
                            /[^0-9]/g,
                            ""
                          );

                        setValue(
                          "mobile_number",
                          onlyNums,
                          {
                            shouldValidate:
                              true,
                          }
                        );
                      },
                    }
                  )}

                  error={
                    !!errors.mobile_number
                  }

                  className={`${inputBaseStyles} rounded-l-none !border-t-blue-gray-200 focus:!border-amber-500`}

                  labelProps={
                    labelProps
                  }

                  containerProps={{
                    className:
                      "min-w-0",
                  }}

                />

              </div>


              {(
                errors.mobile_number ||
                errors.country_code
              ) && (

                  <span className="text-[#FF0000] text-xs">

                    {
                      errors
                        .mobile_number
                        ?.message ||
                      errors
                        .country_code
                        ?.message
                    }

                  </span>
                )}

            </div>


            {/* EMAIL */}

            <div className="flex flex-col gap-1.5">

              <Typography
                variant="small"
                className="font-lato text-[#2F302C] text-[16px]"
              >

                {t("fields.email")}

                <span className="text-[#FF0000]">
                  *
                </span>

              </Typography>


              <Input
                size="lg"
                placeholder={t(
                  "placeholders.email"
                )}

                {...register(
                  "email",
                  {
                    onChange: (e) =>
                      handleNoLeadingSpace(
                        e,
                        "email"
                      ),
                  }
                )}

                error={
                  !!errors.email
                }

                className={
                  inputBaseStyles
                }

                labelProps={
                  labelProps
                }
              />


              {errors.email && (

                <span className="text-[#FF0000] text-xs">

                  {
                    errors.email.message
                  }

                </span>
              )}

            </div>

          </div>


          {/* =================================================
              ROW 3 - PASSWORD
          ================================================= */}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">


            {/* PASSWORD */}

            <div className="flex flex-col gap-1.5">

              <Typography
                variant="small"
                className="font-lato text-[#2F302C] text-[16px]"
              >

                {t("fields.password")}

                <span className="text-[#FF0000]">
                  *
                </span>

              </Typography>


              <Input
                type={
                  showPassword
                    ? "text"
                    : "password"
                }

                size="lg"

                placeholder={t(
                  "placeholders.password"
                )}

                {...register(
                  "password",
                  {
                    onChange: (e) =>
                      handleNoLeadingSpace(
                        e,
                        "password"
                      ),
                  }
                )}

                error={
                  !!errors.password
                }

                className={
                  inputBaseStyles
                }

                labelProps={
                  labelProps
                }

                icon={
                  showPassword ? (

                    <EyeIcon
                      className="h-5 w-5 cursor-pointer"
                      onClick={() =>
                        setShowPassword(
                          false
                        )
                      }
                    />

                  ) : (

                    <EyeSlashIcon
                      className="h-5 w-5 cursor-pointer"
                      onClick={() =>
                        setShowPassword(
                          true
                        )
                      }
                    />

                  )
                }

              />


              {errors.password && (

                <span className="text-[#FF0000] text-xs whitespace-pre-line">

                  {
                    errors
                      .password
                      .message
                  }

                </span>
              )}

            </div>


            {/* CONFIRM PASSWORD */}

            <div className="flex flex-col gap-1.5">

              <Typography
                variant="small"
                className="font-lato text-[#2F302C] text-[16px]"
              >

                {t(
                  "fields.confirmPassword"
                )}

                <span className="text-[#FF0000]">
                  *
                </span>

              </Typography>


              <Input
                type={
                  showConfirmPassword
                    ? "text"
                    : "password"
                }

                size="lg"

                placeholder={t(
                  "placeholders.confirmPassword"
                )}

                {...register(
                  "password_confirmation",
                  {
                    onChange: (e) =>
                      handleNoLeadingSpace(
                        e,
                        "password_confirmation"
                      ),
                  }
                )}

                error={
                  !!errors.password_confirmation
                }

                className={
                  inputBaseStyles
                }

                labelProps={
                  labelProps
                }

                icon={
                  showConfirmPassword ? (

                    <EyeIcon
                      className="h-5 w-5 cursor-pointer"
                      onClick={() =>
                        setShowConfirmPassword(
                          false
                        )
                      }
                    />

                  ) : (

                    <EyeSlashIcon
                      className="h-5 w-5 cursor-pointer"
                      onClick={() =>
                        setShowConfirmPassword(
                          true
                        )
                      }
                    />

                  )
                }

              />


              {errors.password_confirmation && (

                <span className="text-[#FF0000] text-xs">

                  {
                    errors
                      .password_confirmation
                      .message
                  }

                </span>
              )}

            </div>

          </div>


          {/* =================================================
              ROW 4 - COUNTRY / CITY
          ================================================= */}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">


            {/* COUNTRY */}

            <div className="flex flex-col gap-1.5">

              <Typography
                variant="small"
                className="font-lato text-[#2F302C] text-[16px]"
              >

                {t("fields.country")}

                <span className="text-[#FF0000]">
                  *
                </span>

              </Typography>


              <Select

                placeholder={t(
                  "placeholders.country"
                )}

                onChange={(country) => {

                  setValue(
                    "country_id",
                    country?.value,
                    {
                      shouldValidate:
                        true,
                    }
                  );

                  dispatch(
                    fetchCity(
                      country?.value
                    )
                  );
                }}

                options={countries}

                value={
                  countries?.find(
                    (opt) =>
                      opt.value ===
                      watch(
                        "country_id"
                      )
                  ) || null
                }

                isClearable

                noOptionsMessage={() =>
                  t(
                    "select.noOptions"
                  )
                }

              />


              {errors.country_id && (

                <span className="text-[#FF0000] text-xs">

                  {
                    errors
                      .country_id
                      .message
                  }

                </span>
              )}

            </div>


            {/* CITY */}

            <div className="flex flex-col gap-1.5">

              <Typography
                variant="small"
                className="font-lato text-[#2F302C] text-[16px]"
              >

                {t("fields.city")}

                <span className="text-[#FF0000]">
                  *
                </span>

              </Typography>


              <Select

                placeholder={t(
                  "placeholders.city"
                )}

                onChange={(city) => {

                  setValue(
                    "city_id",
                    city?.value,
                    {
                      shouldValidate:
                        true,
                    }
                  );
                }}

                options={cities}

                value={
                  cities?.find(
                    (opt) =>
                      opt.value ===
                      watch(
                        "city_id"
                      )
                  ) || null
                }

                isClearable

                noOptionsMessage={() =>
                  t(
                    "select.noOptions"
                  )
                }

              />


              {errors.city_id && (

                <span className="text-[#FF0000] text-xs">

                  {
                    errors
                      .city_id
                      .message
                  }

                </span>
              )}

            </div>

          </div>


          {/* =================================================
              ADDRESS / GENDER
          ================================================= */}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">


            {/* ADDRESS */}

            <div className="flex flex-col gap-1.5">

              <Typography
                variant="small"
                className="font-lato text-[#2F302C] text-[16px]"
              >

                {t("fields.address")}

                <span className="text-[#FF0000]">
                  *
                </span>

              </Typography>


              <Input
                size="lg"
                placeholder={t(
                  "placeholders.address"
                )}

                {...register(
                  "permanent_address",
                  {
                    onChange: (e) =>
                      handleNoLeadingSpace(
                        e,
                        "permanent_address"
                      ),
                  }
                )}

                error={
                  !!errors.permanent_address
                }

                className={
                  inputBaseStyles
                }

                labelProps={
                  labelProps
                }
              />


              {errors.permanent_address && (

                <span className="text-[#FF0000] text-xs">

                  {
                    errors
                      .permanent_address
                      .message
                  }

                </span>
              )}

            </div>


            {/* GENDER */}

            <div className="flex flex-col gap-1.5">

              <Typography
                variant="small"
                className="font-lato text-[#2F302C] text-[16px]"
              >

                {t("fields.gender")}

                <span className="text-[#FF0000]">
                  *
                </span>

              </Typography>


              <Select

                placeholder={t(
                  "placeholders.gender"
                )}

                options={
                  genderOptions
                }

                onChange={(option) => {

                  setValue(
                    "gender",
                    option?.value,
                    {
                      shouldValidate:
                        true,
                    }
                  );
                }}

                value={
                  genderOptions.find(
                    (opt) =>
                      opt.value ===
                      watch("gender")
                  ) || null
                }

                isClearable

                styles={{
                  control: (
                    base,
                    state
                  ) => ({
                    ...base,
                    height: "44px",
                    minHeight: "44px",
                    borderColor:
                      errors.gender
                        ? "#FF0000"
                        : "#CFD2D9",
                    boxShadow:
                      state.isFocused
                        ? "0 0 0 1px #333"
                        : "none",
                    "&:hover": {
                      borderColor:
                        "#90A4AE",
                    },
                  }),
                }}

              />


              {errors.gender && (

                <span className="text-[#FF0000] text-xs">

                  {
                    errors.gender.message
                  }

                </span>
              )}

            </div>

          </div>


          {/* =================================================
              RESIDENCE / DRIVING LICENSE
          ================================================= */}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">


            {/* RESIDENCE */}

            <div className="flex flex-col gap-1.5 font-lato">

              <Typography className="font-lato text-[#2F302C] text-[16px]">

                {t(
                  "fields.residenceId"
                )}

                <span className="text-[#FF0000]">
                  *
                </span>

              </Typography>


              <div className="flex h-11 italic">

                <Input
                  size="lg"

                  placeholder={t(
                    "placeholders.residenceId"
                  )}

                  {...register(
                    "residence_id",
                    {
                      onChange: (e) =>
                        handleNoLeadingSpace(
                          e,
                          "residence_id"
                        ),
                    }
                  )}

                  className={`${inputBaseStyles} !border-[#BDD9CD] rounded-r-none h-full focus:!border-amber-500`}

                  containerProps={{
                    className:
                      "min-w-0",
                  }}

                  labelProps={
                    labelProps
                  }

                />


                <Button
                  type="button"
                  onClick={() =>
                    residenceRef.current?.click()
                  }

                  className="flex flex-shrink-0 items-center gap-2 bg-[#065C3626] text-[#065C36] border border-dashed border-[#065C36] shadow-none rounded-l-none normal-case px-3 text-[13px] font-bold h-full"
                >

                  <DocumentArrowUpIcon className="h-4 w-4 stroke-[2.5px]" />

                  {t(
                    "buttons.chooseFile"
                  )}

                </Button>


                <input
                  type="file"
                  className="hidden"
                  accept="image/*,application/pdf"
                  ref={residenceRef}

                  onChange={(e) => {

                    const file =
                      e.target.files?.[0];

                    if (file) {

                      setValue(
                        "residence_image",
                        file,
                        {
                          shouldValidate:
                            true,
                        }
                      );
                    }
                  }}

                />

              </div>


              {(
                errors.residence_id ||
                errors.residence_image
              ) && (

                  <span className="text-[#FF0000] text-xs">

                    {
                      errors
                        .residence_id
                        ?.message ||
                      errors
                        .residence_image
                        ?.message
                    }

                  </span>
                )}


              <div className="mt-3">

                {residence_image && (

                  <div className="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300">

                    <FilePreview

                      file={
                        residence_image
                      }

                      onRemove={() =>
                        setValue(
                          "residence_image",
                          null,
                          {
                            shouldValidate:
                              false,
                          }
                        )
                      }

                      isView={false}

                      containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"

                    />

                  </div>

                )}

              </div>

            </div>


            {/* DRIVING LICENSE */}

            <div className="flex flex-col gap-1.5">

              <Typography
                variant="small"
                className="font-lato text-[#2F302C] text-[16px]"
              >

                {t(
                  "fields.drivingLicense"
                )}

                <span className="text-[#FF0000]">
                  *
                </span>

              </Typography>


              <div className="flex h-11 italic">

                <Input
                  size="lg"

                  placeholder={t(
                    "placeholders.drivingLicense"
                  )}

                  readOnly

                  className={`${inputBaseStyles} !border-[#BDD9CD] rounded-r-none h-full focus:!border-amber-500 cursor-not-allowed`}

                  containerProps={{
                    className:
                      "min-w-0",
                  }}

                  labelProps={
                    labelProps
                  }

                />


                <Button
                  type="button"
                  onClick={() =>
                    licenseRef.current?.click()
                  }

                  className="flex flex-shrink-0 items-center gap-2 bg-[#065C3626] text-[#065C36] border border-dashed border-[#065C36] shadow-none rounded-l-none normal-case px-3 text-[13px] font-bold h-full"
                >

                  <DocumentArrowUpIcon className="h-4 w-4" />

                  {t(
                    "buttons.chooseFile"
                  )}

                </Button>


                <input
                  type="file"
                  className="hidden"
                  accept="image/*,application/pdf"
                  ref={licenseRef}

                  onChange={(e) => {

                    const file =
                      e.target.files?.[0];

                    if (file) {

                      setValue(
                        "driving_licence_image",
                        file,
                        {
                          shouldValidate:
                            true,
                        }
                      );
                    }
                  }}

                />

              </div>


              {errors.driving_licence_image && (

                <span className="text-[#FF0000] text-xs">

                  {
                    errors
                      .driving_licence_image
                      .message
                  }

                </span>
              )}


              <div className="mt-3">

                {driving_licence_image && (

                  <div className="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300">

                    <FilePreview

                      file={
                        driving_licence_image
                      }

                      onRemove={() =>
                        setValue(
                          "driving_licence_image",
                          null,
                          {
                            shouldValidate:
                              false,
                          }
                        )
                      }

                      isView={false}

                      containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"

                    />

                  </div>

                )}

              </div>

            </div>

          </div>


          {/* =================================================
              VEHICLE / INSURANCE
          ================================================= */}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">


            {/* VEHICLE */}

            <div className="flex flex-col gap-1.5">

              <Typography
                variant="small"
                className="font-lato text-[#2F302C] text-[16px]"
              >

                {t(
                  "fields.vehicleNumber"
                )}

              </Typography>


              <div className="flex h-11 italic">

                <Input
                  size="lg"

                  placeholder={t(
                    "placeholders.vehicleNumber"
                  )}

                  {...register(
                    "vehicle_number",
                    {
                      onChange: (e) =>
                        handleNoLeadingSpace(
                          e,
                          "vehicle_number"
                        ),
                    }
                  )}

                  className={`${inputBaseStyles} !border-[#BDD9CD] rounded-r-none h-full focus:!border-amber-500`}

                  containerProps={{
                    className:
                      "min-w-0",
                  }}

                  labelProps={
                    labelProps
                  }

                />


                <Button
                  type="button"
                  onClick={() =>
                    vehicleCertificateRef.current?.click()
                  }

                  className="flex flex-shrink-0 items-center gap-2 bg-[#065C3626] text-[#065C36] border border-dashed border-[#065C36] shadow-none rounded-l-none normal-case px-3 text-[13px] font-bold h-full"
                >

                  <DocumentArrowUpIcon className="h-4 w-4 stroke-[2.5px]" />

                  {t(
                    "buttons.chooseFile"
                  )}

                </Button>


                <input
                  type="file"
                  className="hidden"
                  accept="image/*,application/pdf"
                  ref={
                    vehicleCertificateRef
                  }

                  onChange={(e) => {

                    const file =
                      e.target.files?.[0];

                    if (file) {

                      setValue(
                        "vehicle_certificate_image",
                        file,
                        {
                          shouldValidate:
                            true,
                        }
                      );
                    }
                  }}

                />

              </div>


              {(
                errors.vehicle_number ||
                errors.vehicle_certificate_image
              ) && (

                  <span className="text-[#FF0000] text-xs">

                    {
                      errors
                        .vehicle_number
                        ?.message ||
                      errors
                        .vehicle_certificate_image
                        ?.message
                    }

                  </span>
                )}


              <div className="mt-3">

                {vehicle_certificate_image && (

                  <div className="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300">

                    <FilePreview

                      file={
                        vehicle_certificate_image
                      }

                      onRemove={() =>
                        setValue(
                          "vehicle_certificate_image",
                          null,
                          {
                            shouldValidate:
                              true,
                          }
                        )
                      }

                      isView={false}

                      containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"

                    />

                  </div>

                )}

              </div>

            </div>


            {/* INSURANCE */}

            <div className="flex flex-col gap-1.5">

              <Typography
                variant="small"
                className="font-lato text-[#2F302C] text-[16px]"
              >

                {t(
                  "fields.insurance"
                )}

              </Typography>


              <div className="flex h-11 italic">

                <Input
                  size="lg"

                  placeholder={t(
                    "placeholders.insurance"
                  )}

                  readOnly

                  className={`${inputBaseStyles} !border-[#BDD9CD] rounded-r-none h-full focus:!border-amber-500 cursor-not-allowed`}

                  containerProps={{
                    className:
                      "min-w-0",
                  }}

                  labelProps={
                    labelProps
                  }

                />


                <Button
                  type="button"
                  onClick={() =>
                    insuranceRef.current?.click()
                  }

                  className="flex flex-shrink-0 items-center gap-2 bg-[#065C3626] text-[#065C36] border border-dashed border-[#065C36] shadow-none rounded-l-none normal-case px-3 text-[13px] font-bold h-full"
                >

                  <DocumentArrowUpIcon className="h-4 w-4" />

                  {t(
                    "buttons.chooseFile"
                  )}

                </Button>


                <input
                  type="file"
                  className="hidden"
                  accept="image/*,application/pdf"
                  ref={insuranceRef}

                  onChange={(e) => {

                    const file =
                      e.target.files?.[0];

                    if (file) {

                      setValue(
                        "insurance_image",
                        file,
                        {
                          shouldValidate:
                            true,
                        }
                      );
                    }
                  }}

                />

              </div>


              {errors.insurance_image && (

                <span className="text-[#FF0000] text-xs">

                  {
                    errors
                      .insurance_image
                      .message
                  }

                </span>
              )}


              <div className="mt-3">

                {insurance_image && (

                  <div className="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300">

                    <FilePreview

                      file={
                        insurance_image
                      }

                      onRemove={() =>
                        setValue(
                          "insurance_image",
                          null,
                          {
                            shouldValidate:
                              false,
                          }
                        )
                      }

                      isView={false}

                      containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"

                    />

                  </div>

                )}

              </div>

            </div>


            {/* OTHER DOCUMENTS */}

            <div className="flex flex-col gap-1.5 col-span-1 md:col-span-2">

              <Typography
                variant="small"
                className="font-lato text-[#2F302C] text-[16px]"
              >

                {t(
                  "fields.otherDocuments"
                )}

              </Typography>


              <div className="flex h-11 italic">

                <Input

                  size="lg"

                  placeholder={t(
                    "placeholders.otherDocuments"
                  )}

                  {...register(
                    "other_documents_detail",
                    {
                      onChange: (e) =>
                        handleNoLeadingSpace(
                          e,
                          "other_documents_detail"
                        ),
                    }
                  )}

                  className={`${inputBaseStyles} !border-[#BDD9CD] rounded-r-none h-full focus:!border-amber-500`}

                  containerProps={{
                    className:
                      "min-w-0",
                  }}

                  labelProps={
                    labelProps
                  }

                />


                <Button
                  type="button"

                  onClick={() =>
                    otherDocumentsRef.current?.click()
                  }

                  className="flex flex-shrink-0 items-center gap-2 bg-[#065C3626] text-[#065C36] border border-dashed border-[#065C36] shadow-none rounded-l-none normal-case px-3 text-[13px] font-bold h-full"
                >

                  <DocumentArrowUpIcon className="h-4 w-4 stroke-[2.5px]" />

                  {t(
                    "buttons.chooseFile"
                  )}

                </Button>


                <input

                  type="file"

                  multiple

                  accept="image/*,application/pdf"

                  ref={
                    otherDocumentsRef
                  }

                  className="hidden"

                  onChange={(e) =>
                    handleFileChange(
                      e,
                      "other_documents"
                    )
                  }

                />

              </div>


              {(
                errors.other_documents_detail ||
                errors.other_documents
              ) && (

                  <span className="text-[#FF0000] text-xs">

                    {
                      errors
                        .other_documents_detail
                        ?.message ||

                      errors
                        .other_documents
                        ?.message
                    }

                  </span>
                )}


              <div className="mt-3 flex flex-wrap gap-6">

                {other_documents?.length > 0 &&

                  other_documents.map(
                    (file, index) => (

                      <div
                        key={index}
                        className="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"
                      >

                        <FilePreview

                          file={file}

                          onRemove={() => {

                            const updated =
                              other_documents.filter(
                                (_, i) =>
                                  i !== index
                              );

                            setValue(
                              "other_documents",
                              updated,
                              {
                                shouldValidate:
                                  true,
                              }
                            );
                          }}

                          isView={false}

                          containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"

                        />

                      </div>

                    )
                  )}

              </div>

            </div>

          </div>


          {/* =================================================
              TERMS
          ================================================= */}

          <div className="flex flex-col gap-1.5">

            <Checkbox

              color="green"

              {...register(
                "agree_terms"
              )}

              label={

                <Typography className="text-sm font-medium text-gray-700 underline">

                  {t(
                    "fields.agreeTerms"
                  )}

                </Typography>
              }

            />


            {errors.agree_terms && (

              <span className="text-[#FF0000] text-xs">

                {
                  errors
                    .agree_terms
                    .message
                }

              </span>
            )}


            <Button

              type="submit"

              fullWidth

              className="bg-[#F2AD25] py-4 mt-4 text-sm font-lato shadow-none hover:shadow-none rounded-md"

              disabled={loading}

            >

              {t(
                "buttons.requestAccount"
              )}

            </Button>


            <Typography className="text-center text-sm font-medium text-gray-600">

              {t(
                "messages.alreadyApproved"
              )}

              <a
                href="/sign-in"
                className="text-[#F2AD25] font-lato pl-1"
              >

                {t(
                  "buttons.login"
                )}

              </a>

            </Typography>

          </div>

        </form>


        {/* ===================================================
            OTP MODAL
        =================================================== */}

        {openModal && (

          <OTPVerification

            isOpen={openModal}

            onClose={() =>
              closeVerifyOtpModal()
            }

            onSuccess={() =>
              success()
            }

            registrationFormData={
              registrationFormData
            }

          />

        )}


        {/* ===================================================
            CONFIRMATION MODAL
        =================================================== */}

        {isConfirmationModalOpen && (

          <ConfirmationModal

            isOpen={
              isConfirmationModalOpen
            }

            onConfirmClose={() =>
              confirmed()
            }

          />

        )}

      </div>
      <CountryDetection/>
    </div>
  );
}

export default SignUp;